import { deleteHttp, post, put } from "../../../../common/api/apiShared";
import { useExpandedId } from "../../../../common/hooks/useExpandedId";
import { useSelectableLegalEntityQuery } from "../../../../legalEntity/useSelectableLegalEntityQuery";
import { ContractModel, CounterPartySubContractorViewModel, CounterPartyUpdateModel, CounterPartyViewModel } from "../../Contract.types";
import { ICounterPartiesStepProps } from "./CounterPartiesStep";
import { getContractQueryKey } from "../../Contract.hooks";
import { useOptimisticUpdate } from "../../../../common/hooks/useOptimisticUpdate";
import { ContractPartyKind, SubContractorUpdateModel } from "./CounterPartiesStep.types";
import { useRef } from "react";
import { ajaxQueue } from "../../../../common/api/ajaxQueue";
import { useDotLegalQueryClient, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export default function useCounterPartiesStepHooks(props: ICounterPartiesStepProps) {
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const queryClient = useDotLegalQueryClient();
    const { getExpandedId, setExpandedId } = useExpandedId();
    const query = useSelectableLegalEntityQuery();
    const queryKey = getContractQueryKey(props.contractId);

    const legalEntityData = query.data;

    // The list a party belongs to is derived from whether its legal entity is a group company, so a party carrying no
    // entity has no column of its own. Those only exist as leftovers from the previous add flow, which persisted a row
    // before an entity was picked - clearing the picker deletes the row instead. They start out external, and once an
    // entity has been seen the remembered kind keeps the row where it was last shown.
    const lastKnownPartyKind = useRef(new Map<string, ContractPartyKind>());

    function getPartyKind(counterParty: CounterPartyViewModel) {
        const legalEntity = legalEntityData?.find((x) => x.id === counterParty.legalEntityId);

        if (legalEntity) {
            const kind = legalEntity.isCustomerOwned ? ContractPartyKind.Internal : ContractPartyKind.External;
            lastKnownPartyKind.current.set(counterParty.id, kind);
            return kind;
        }

        return lastKnownPartyKind.current.get(counterParty.id) ?? ContractPartyKind.External;
    }

    // The split can only be made once the entity list has loaded - splitting earlier would put every party in the
    // external list and then visibly re-sort them.
    const partiesOfKind = (kind: ContractPartyKind) => (legalEntityData ? props.contract.counterParties.filter((x) => getPartyKind(x) === kind) : []);

    const internalParties = partiesOfKind(ContractPartyKind.Internal);
    const externalParties = partiesOfKind(ContractPartyKind.External);

    function onExpandClick(id: string) {
        if (getExpandedId() === id) {
            setExpandedId("");
        } else {
            setExpandedId(id);
        }
    }

    const removeCounterPartyMutation = useDotLegalMutation(removeCounterPartyAPI, {
        onSuccess: (data) => {
            const contract = data.value();
            queryClient.setQueryData(queryKey, contract);
        },
    });

    const removeCounterParty = async (id: string) => {
        await removeCounterPartyMutation.mutateAsync(id);
    };

    async function removeCounterPartyAPI(id: string) {
        return await deleteHttp(`/contract/${props.contractId}/counterParties/${id}`);
    }

    const addCounterPartyMutation = useDotLegalMutation(addCounterPartyAPI, {
        onSuccess: (data) => {
            const contract = data.value();
            queryClient.setQueryData(queryKey, contract);

            const lastItem = contract.counterParties[contract.counterParties.length - 1];
            setExpandedId(lastItem.id);
        },
    });

    const addCounterParty = async (legalEntityId: string) => {
        await addCounterPartyMutation.mutateAsync(legalEntityId);
    };

    async function addCounterPartyAPI(legalEntityId: string) {
        return await post<ContractModel>(`/contract/${props.contractId}/counterParties`, legalEntityId);
    }

    const updateCounterPartyMutation = useDotLegalMutation(updateCounterPartyAPI, {
        onSuccess: (data) => {
            const contract = data.value();
            queryClient.setQueryData(queryKey, contract);
        },
    });

    async function onCounterPartyChange(counterPartyId: string, changeFunc: (counterPartyViewModel: CounterPartyViewModel) => void) {
        const tempContact = { ...props.contract! };

        var counterParty = tempContact.counterParties.find((x) => x.id === counterPartyId)!;

        changeFunc(counterParty);

        await ajaxQueue().addRequest(updateCounterPartyMutation.mutateAsync, counterParty);
    }

    async function onSubContractorChange(
        counterPartyId: string,
        subContractoId: string,
        changeFunc: (subContractor: CounterPartySubContractorViewModel) => void
    ) {
        const tempContact = { ...props.contract! };

        var counterParty = tempContact.counterParties.find((x) => x.id === counterPartyId)!;
        var subcontractor = counterParty.subContractors.find((x) => x.id === subContractoId)!;

        changeFunc(subcontractor);
        await updateSubContractory(tempContact, subcontractor);
    }

    async function updateSubContractory(countract: ContractModel, subContractor: CounterPartySubContractorViewModel) {
        await putOnQueueAndSetQueryData(countract, queryKey, updateSubContractorAPI, subContractor);
    }

    async function updateCounterPartyAPI(viewModel: CounterPartyViewModel) {
        return await put<ContractModel>("/contract/" + props.contractId + "/counterParties/" + viewModel.id, convertToContractPartyUpdateModel(viewModel));
    }

    async function updateSubContractorAPI(viewModel: CounterPartySubContractorViewModel) {
        return await put<ContractModel>(
            `/contract/${props.contractId}/counterParties/${viewModel.counterPartyId}/subcontractor/${viewModel.id}`,
            convertToSubContractorUpdateModel(viewModel)
        );
    }

    return {
        onExpandClick,
        onExpand: setExpandedId,
        expandedId: getExpandedId(),
        legalEntityData,
        internalOptions: legalEntityData?.filter((x) => x.isCustomerOwned),
        externalOptions: legalEntityData?.filter((x) => !x.isCustomerOwned),
        internalParties,
        externalParties,
        legalEntityIsLoading: query.isLoading,
        addCounterParty,
        removeCounterParty,
        isAddingCounterParty: addCounterPartyMutation.isLoading,
        onCounterPartyChange,
        onSubContractorChange,
    };

    function convertToSubContractorUpdateModel(viewModel: CounterPartySubContractorViewModel) {
        const updateModel: SubContractorUpdateModel = {
            legalEntityId: viewModel.legalEntityId,
            link: viewModel.link,
            hasAgreement: viewModel.hasAgreement,
        };

        return updateModel;
    }

    function convertToContractPartyUpdateModel(viewModel: CounterPartyViewModel) {
        const updateModel: CounterPartyUpdateModel = {
            legalEntityId: viewModel.legalEntityId,
        };

        return updateModel;
    }
}
